// Real Web Push: service worker + VAPID + backend subscription persistence.
// Falls back gracefully if the browser lacks Push/Service Worker support.
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const SW_URL = "/sw.js";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

async function registerSW() {
  if (!("serviceWorker" in navigator)) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_URL);
  if (existing) return existing;
  return await navigator.serviceWorker.register(SW_URL);
}

export async function getPushSubscription() {
  if (!isPushSupported()) return null;
  const reg = await registerSW();
  if (!reg) return null;
  return await reg.pushManager.getSubscription();
}

async function fetchPublicKey() {
  const r = await api.get("/push/public-key");
  return r.data?.public_key;
}

export async function enableWebPush({ portalToken } = {}) {
  if (!isPushSupported()) throw new Error("Push not supported in this browser");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission denied");
  const reg = await registerSW();
  await navigator.serviceWorker.ready;
  const pubKey = await fetchPublicKey();
  if (!pubKey) throw new Error("Server VAPID key unavailable");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(pubKey),
    });
  }
  const path = portalToken ? `/push/portal/${portalToken}/subscribe` : "/push/subscribe";
  await api.post(path, { subscription: sub.toJSON() });
  return sub;
}

export async function disableWebPush() {
  if (!isPushSupported()) return;
  const sub = await getPushSubscription();
  if (!sub) return;
  try {
    await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`);
  } catch (err) { console.debug("Push unsubscribe DELETE ignored:", err?.message || err); }
  try { await sub.unsubscribe(); } catch (err) { console.debug("Push local unsubscribe failed:", err?.message || err); }
}

// React hook: exposes permission state, subscription state and enable/disable actions.
export function usePushNotifications(enabled) {
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [supported] = useState(isPushSupported());

  useEffect(() => {
    if (!enabled || !supported) return;
    (async () => {
      try {
        await registerSW();
        const sub = await getPushSubscription();
        setSubscribed(!!sub);
      } catch (e) { /* ignore */ }
    })();
  }, [enabled, supported]);

  // Listen for SW navigate messages (when a push notification is clicked)
  useEffect(() => {
    if (!supported) return;
    const onMessage = (event) => {
      const url = event.data?.url;
      if (event.data?.type === "navigate" && url) {
        try {
          if (url.startsWith("http")) window.location.href = url;
          else window.location.assign(url);
        } catch (e) { /* ignore */ }
      }
    };
    navigator.serviceWorker?.addEventListener?.("message", onMessage);
    return () => navigator.serviceWorker?.removeEventListener?.("message", onMessage);
  }, [supported]);

  const enable = useCallback(async () => {
    const sub = await enableWebPush();
    setPermission(Notification.permission);
    setSubscribed(!!sub);
    return sub;
  }, []);

  const disable = useCallback(async () => {
    await disableWebPush();
    setSubscribed(false);
  }, []);

  const request = enable; // backward compat with previous API

  return { supported, permission, subscribed, enable, disable, request };
}
